import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { LuCheck, LuListChecks, LuMinus, LuTrash2, LuX } from 'react-icons/lu'
import { faPlain } from 'utilities/helper/format'

/**
 * What is ticked: some rows by id, or — `all` — the whole filtered list,
 * including the older pages that were never loaded.
 */
export interface Selection {
    all: boolean
    ids: ReadonlySet<number>
}

export const NOTHING_SELECTED: Selection = { all: false, ids: new Set() }

/** The bar is this tall, so the day labels can stick right under it. */
export const SELECTION_BAR_CLASS = 'h-[52px]'
export const DAY_LABEL_TOP_SELECTING = 'top-[7.25rem]'

/** «انتخاب» at the end of the row above the list. */
export function SelectButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex h-7 shrink-0 items-center gap-1 rounded-full bg-divider px-2.5 text-[11px] font-bold text-gray-1 transition-colors active:bg-stroke md:text-xs"
        >
            <LuListChecks className="h-3.5 w-3.5" />
            انتخاب
        </button>
    )
}

/**
 * The round-cornered box that takes the ⋮ button's place on every card while
 * the member is choosing what to delete.
 */
export function SelectBox({ checked }: { checked: boolean }) {
    return (
        <span
            aria-hidden
            className={classNames(
                '-me-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-colors duration-150',
                checked
                    ? 'border-green-1 bg-green-1 text-white'
                    : 'border-stroke bg-white text-transparent'
            )}
        >
            <LuCheck className="h-4 w-4" strokeWidth={3} />
        </span>
    )
}

interface SelectionBarProps {
    /** Ticked rows; ignored while the whole list is selected. */
    count: number
    wholeList: boolean
    /** Every loaded row is ticked (or the whole list is). */
    allLoaded: boolean
    onToggleAll: () => void
    onDelete: () => void
    onCancel: () => void
}

/**
 * Sticks under the page header while the member scrolls and ticks, so «حذف»
 * is always one tap away.
 */
export function SelectionBar({
    count,
    wholeList,
    allLoaded,
    onToggleAll,
    onDelete,
    onCancel,
}: SelectionBarProps) {
    const some = wholeList || count > 0
    return (
        <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className={classNames(
                'sticky top-14 z-20 flex items-center gap-1 rounded-card border border-green-1/30 bg-white px-1.5 shadow-badge',
                SELECTION_BAR_CLASS
            )}
        >
            <button
                type="button"
                onClick={onCancel}
                aria-label="لغو انتخاب"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-text-secondary transition-colors active:bg-divider"
            >
                <LuX className="h-5 w-5" />
            </button>
            <p
                aria-live="polite"
                className="min-w-0 flex-1 truncate text-[13px] font-bold text-heading md:text-sm"
            >
                {wholeList
                    ? 'همهٔ پیام‌های این فهرست'
                    : count > 0
                    ? `${faPlain.format(count)} مورد انتخاب شد`
                    : 'پیام‌ها را انتخاب کنید'}
            </p>
            <button
                type="button"
                role="checkbox"
                aria-checked={allLoaded ? true : some ? 'mixed' : false}
                onClick={onToggleAll}
                className="flex h-9 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-bold text-text-secondary transition-colors active:bg-divider md:text-[13px]"
            >
                <span
                    aria-hidden
                    className={classNames(
                        'flex h-[18px] w-[18px] items-center justify-center rounded-md border-2 transition-colors',
                        some
                            ? 'border-green-1 bg-green-1 text-white'
                            : 'border-stroke bg-white'
                    )}
                >
                    {allLoaded ? (
                        <LuCheck className="h-3.5 w-3.5" strokeWidth={3} />
                    ) : some ? (
                        <LuMinus className="h-3.5 w-3.5" strokeWidth={3} />
                    ) : null}
                </span>
                همه
            </button>
            <button
                type="button"
                onClick={onDelete}
                disabled={!some}
                className="flex h-9 shrink-0 items-center gap-1 rounded-full bg-danger px-3 text-[12px] font-bold text-white transition-opacity active:opacity-90 disabled:opacity-40 md:text-[13px]"
            >
                <LuTrash2 className="h-4 w-4" />
                حذف
            </button>
        </motion.div>
    )
}

/**
 * Under the bar, once every loaded row is ticked but older pages remain: the
 * way to take the whole list, like a mail app's «select all conversations».
 */
export function WholeListHint({
    visible,
    wholeList,
    loadedCount,
    onSelectWholeList,
}: {
    visible: boolean
    wholeList: boolean
    loadedCount: number
    onSelectWholeList: () => void
}) {
    return (
        <AnimatePresence initial={false}>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                >
                    <div className="rounded-card bg-brand-soft/60 px-3 py-2.5 text-center text-[12px] leading-6 text-text-body md:text-[13px]">
                        {wholeList ? (
                            <>
                                همهٔ پیام‌های این فهرست انتخاب شد، حتی
                                قدیمی‌ترهایی که هنوز روی صفحه نیامده‌اند.
                            </>
                        ) : (
                            <>
                                {faPlain.format(loadedCount)} پیامِ روی صفحه
                                انتخاب شد.{' '}
                                <button
                                    type="button"
                                    onClick={onSelectWholeList}
                                    className="font-bold text-green-1 underline decoration-green-1/40 underline-offset-4"
                                >
                                    انتخاب همهٔ پیام‌های این فهرست
                                </button>
                            </>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
